import { IconType } from 'react-icons';
import {
    TbAddressBook,
    TbAlignLeft,
    TbBuildingEstate,
    TbCategory2,
    TbChartInfographic,
    TbChecklist,
    TbCube,
    TbFileDescription,
    TbFiles,
    TbFolder,
    TbFolderOpen,
    TbFolderPlus,
    TbFolders,
    TbHierarchy,
    TbLayout,
    TbLayoutGrid,
    TbListCheck,
    TbMist,
    TbRoute,
    TbUsers,
    TbUsersGroup,
} from 'react-icons/tb';

import ListViewIcon from '@/shared/components/icons/ListViewIcon';

export type WorkspaceResourceKind =
    | 'dashboard'
    | 'folder'
    | 'folders'
    | 'investment'
    | 'lifecycle'
    | 'list'
    | 'listView'
    | 'member'
    | 'memo'
    | 'pipeline'
    | 'report'
    | 'reportComponent'
    | 'resource'
    | 'task'
    | 'team'
    | 'workspace';

export type WorkspaceConceptKind = 'lifecycleCategory' | 'lifecycleTaskGroup';
export type WorkspaceListTypeKind = 'companies' | 'custom' | 'people';
export type WorkspaceFolderGlyphVariant = 'create' | 'default' | 'open';

/**
 * The only registry for primary workspace-resource glyphs. Navigation,
 * enriched references, search, dropdowns, and resource avatars must resolve
 * their icons through this map (or WorkspaceResourceGlyph) to prevent drift.
 */
export const WORKSPACE_RESOURCE_ICON_REGISTRY = {
    dashboard: TbLayout,
    folder: TbFolder,
    folders: TbFolders,
    investment: TbAlignLeft,
    lifecycle: TbHierarchy,
    list: TbMist,
    listView: ListViewIcon,
    member: TbUsersGroup,
    memo: TbFiles,
    pipeline: TbRoute,
    report: TbFileDescription,
    reportComponent: TbChartInfographic,
    resource: TbFiles,
    task: TbListCheck,
    team: TbUsers,
    workspace: TbLayoutGrid,
} satisfies Record<WorkspaceResourceKind, IconType>;

/** Singular folder glyphs share one gold treatment while the plural Folders resource remains neutral. */
export const WORKSPACE_FOLDER_ICON_REGISTRY = {
    create: TbFolderPlus,
    default: TbFolder,
    open: TbFolderOpen,
} satisfies Record<WorkspaceFolderGlyphVariant, IconType>;

/** Canonical glyphs for first-class concepts nested beneath workspace resources. */
export const WORKSPACE_CONCEPT_ICON_REGISTRY = {
    lifecycleCategory: TbCategory2,
    lifecycleTaskGroup: TbChecklist,
} satisfies Record<WorkspaceConceptKind, IconType>;

export const WORKSPACE_LIST_TYPE_ICON_REGISTRY = {
    companies: TbBuildingEstate,
    custom: TbCube,
    people: TbAddressBook,
} satisfies Record<WorkspaceListTypeKind, IconType>;

/** @deprecated Prefer WORKSPACE_RESOURCE_ICON_REGISTRY or WorkspaceResourceGlyph. */
export const workspaceResourceIconTypeMap = WORKSPACE_RESOURCE_ICON_REGISTRY;

const resourceAliases: Record<string, WorkspaceResourceKind> = {
    dashboard: 'dashboard',
    dashboards: 'dashboard',
    folder: 'folder',
    folders: 'folders',
    investment: 'investment',
    investments: 'investment',
    lifecycle: 'lifecycle',
    lifecycles: 'lifecycle',
    list: 'list',
    listview: 'listView',
    listviews: 'listView',
    lists: 'list',
    member: 'member',
    members: 'member',
    memo: 'memo',
    memos: 'memo',
    pipeline: 'pipeline',
    pipelines: 'pipeline',
    report: 'report',
    reportcomponent: 'reportComponent',
    reportcomponents: 'reportComponent',
    reports: 'report',
    resource: 'resource',
    resources: 'resource',
    task: 'task',
    tasks: 'task',
    team: 'team',
    teams: 'team',
    user: 'member',
    users: 'member',
    workspace: 'workspace',
    workspaces: 'workspace',
};

export const normalizeWorkspaceResourceKind = (resource: string): WorkspaceResourceKind => {
    const key = resource
        .trim()
        .replace(/[\s_-]+/g, '')
        .toLowerCase();

    return resourceAliases[key] ?? 'resource';
};

export const getWorkspaceResourceIconType = (resource: string): IconType =>
    WORKSPACE_RESOURCE_ICON_REGISTRY[normalizeWorkspaceResourceKind(resource)] ??
    WORKSPACE_RESOURCE_ICON_REGISTRY.resource;

export const getWorkspaceFolderIconType = (variant: WorkspaceFolderGlyphVariant = 'default'): IconType =>
    WORKSPACE_FOLDER_ICON_REGISTRY[variant];

export const getWorkspaceConceptIconType = (concept: WorkspaceConceptKind): IconType =>
    WORKSPACE_CONCEPT_ICON_REGISTRY[concept];

const listTypeAliases: Record<string, WorkspaceListTypeKind> = {
    companies: 'companies',
    company: 'companies',
    companylist: 'companies',
    companylists: 'companies',
    custom: 'custom',
    customlist: 'custom',
    customlists: 'custom',
    people: 'people',
    peoplelist: 'people',
    peoplelists: 'people',
    person: 'people',
};

export const normalizeWorkspaceListType = (listType: string): WorkspaceListTypeKind | undefined => {
    const key = listType
        .trim()
        .replace(/[\s_-]+/g, '')
        .toLowerCase();

    return listTypeAliases[key];
};
